import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import {
  DatabaseBackup, ShieldCheck, RefreshCw, Loader2, AlertTriangle, CheckCircle,
  XCircle, Clock, Image, HardDriveDownload, Eye, RotateCcw, ArrowLeft, Server, BellRing, Cloud, Link2, Unlink, UploadCloud,
} from 'lucide-react';

const STATUS_STYLE = {
  verified: 'bg-green-100 text-green-800 border-green-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  partial: 'bg-amber-100 text-amber-800 border-amber-200',
  running: 'bg-blue-100 text-blue-800 border-blue-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
};
const KIND_LABEL = {
  database: 'Database backup', media: 'Media backup',
  restore: 'Database restore', 'media-restore': 'Media restore',
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const AdminBackupRecovery = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ runs: [], media: {}, env: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [includeMedia, setIncludeMedia] = useState(true);

  // Google Drive second backup
  const [drive, setDrive] = useState(null);
  const [driveBusy, setDriveBusy] = useState('');
  const [driveMedia, setDriveMedia] = useState(true);

  const loadDrive = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/admin/drive/status');
      setDrive(r.data);
    } catch { /* drive card optional */ }
  }, []);

  // restore modal
  const [restoreBackup, setRestoreBackup] = useState(null);
  const [restoreScope, setRestoreScope] = useState('full');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [restoreMedia, setRestoreMedia] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/admin/backup/history');
      setData(r.data);
    } catch (e) {
      setMsg({ type: 'error', text: 'Could not load backup history.' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDrive(); }, [loadDrive]);
  // gently poll while anything is running
  useEffect(() => {
    const anyRunning = (data.runs || []).some((r) => r.status === 'running');
    if (!anyRunning) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [data.runs, load]);

  const backupNow = async () => {
    setBusy('backup'); setMsg(null);
    try {
      const r = await axiosInstance.post('/admin/backup/run', { includeMedia });
      const st = r.data?.database?.status;
      setMsg(st === 'verified'
        ? { type: 'success', text: `Backup created & verified — ${r.data.database.totalDocs} records.${includeMedia ? ' Media backup running in background.' : ''}` }
        : { type: 'error', text: `Backup finished with status: ${st}` });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Backup failed.' });
    } finally { setBusy(''); }
  };

  const mediaBackup = async () => {
    setBusy('media'); setMsg(null);
    try {
      await axiosInstance.post('/admin/backup/media/run');
      setMsg({ type: 'success', text: 'Media backup started — copying photos in the background.' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: 'Could not start media backup.' });
    } finally { setBusy(''); }
  };

  const openRestore = async (backup) => {
    const scope = backup.scope === 'full' ? 'full' : 'calendar';
    setRestoreBackup(backup); setRestoreScope(scope); setConfirmPhrase('');
    setRestoreMedia(false); setPreview(null); setPreviewLoading(true);
    try {
      const r = await axiosInstance.get(`/admin/backup/${backup.id}/dry-run?scope=${scope}`);
      setPreview(r.data);
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Could not load preview.' });
      setRestoreBackup(null);
    } finally { setPreviewLoading(false); }
  };

  const changeScope = async (scope) => {
    setRestoreScope(scope); setConfirmPhrase(''); setPreview(null); setPreviewLoading(true);
    try {
      const r = await axiosInstance.get(`/admin/backup/${restoreBackup.id}/dry-run?scope=${scope}`);
      setPreview(r.data);
    } finally { setPreviewLoading(false); }
  };

  const doRestore = async () => {
    setRestoring(true); setMsg(null);
    try {
      const r = await axiosInstance.post(`/admin/backup/${restoreBackup.id}/restore`, {
        scope: restoreScope, confirmPhrase, includeMedia: restoreMedia,
      });
      if (r.data.status === 'completed') {
        setMsg({ type: 'success', text: `Restore completed. Emergency backup taken first (${r.data.emergencyBackupId?.slice(0, 8)}…).` });
      } else {
        setMsg({ type: 'error', text: `Restore failed. ${r.data.rolledBack ? 'System was automatically rolled back to its pre-restore state.' : 'ROLLBACK ALSO FAILED — contact support.'}` });
      }
      setRestoreBackup(null);
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Restore failed.' });
    } finally { setRestoring(false); }
  };

  const sendTestAlert = async () => {
    setBusy('test-alert'); setMsg(null);
    try {
      const r = await axiosInstance.post('/admin/backup/test-alert');
      const status = r.data?.result?.status;
      const to = r.data?.recipient;
      if (status === 'sent') {
        setMsg({ type: 'success', text: `Test alert email sent to ${to} (Resend accepted it). No data was changed.` });
      } else if (status === 'logged') {
        setMsg({ type: 'error', text: `Email was logged only, not delivered (recipient ${to}). Check RESEND_API_KEY on this environment.` });
      } else {
        setMsg({ type: 'error', text: `Test alert could not be sent to ${to}: ${r.data?.result?.message || status}` });
      }
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Could not send test alert.' });
    } finally { setBusy(''); }
  };

  const mediaRestore = async () => {
    if (!window.confirm('Restore any MISSING photos/files from the media backup? This never overwrites existing files.')) return;
    if (!window.confirm('Restore any MISSING photos/files from the media backup? This never overwrites existing files.')) return;
    setBusy('media-restore');
    try {
      await axiosInstance.post('/admin/backup/media/restore', { confirmPhrase: 'RESTORE MEDIA' });
      setMsg({ type: 'success', text: 'Media restore started in the background.' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: 'Could not start media restore.' });
    } finally { setBusy(''); }
  };

  const connectDrive = async () => {
    setDriveBusy('connect'); setMsg(null);
    try {
      const r = await axiosInstance.post('/admin/drive/connect');
      const url = r.data?.authorizationUrl;
      if (url) {
        window.open(url, '_blank', 'noopener');
        setMsg({ type: 'success', text: 'A Google authorisation tab has opened. Approve access, then click Refresh here.' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Could not start Google Drive connection.' });
    } finally { setDriveBusy(''); }
  };

  const disconnectDrive = async () => {
    if (!window.confirm('Disconnect Google Drive? Existing Drive copies are kept; no new copies will be made until you reconnect.')) return;
    setDriveBusy('disconnect'); setMsg(null);
    try {
      await axiosInstance.post('/admin/drive/disconnect');
      setMsg({ type: 'success', text: 'Google Drive disconnected.' });
      await loadDrive();
    } catch { setMsg({ type: 'error', text: 'Could not disconnect.' }); }
    finally { setDriveBusy(''); }
  };

  const mirrorNow = async () => {
    setDriveBusy('mirror'); setMsg(null);
    const prevStarted = drive?.lastMirror?.startedAt || '';
    try {
      const r = await axiosInstance.post('/admin/drive/mirror-now', { includeMedia: driveMedia });
      if (r.data?.started === false && r.data?.alreadyRunning) {
        setMsg({ type: 'info', text: 'A Drive copy is already in progress…' });
      } else {
        setMsg({ type: 'info', text: 'In progress… copying to Google Drive (this can take a few minutes for photos).' });
      }
      // Poll status in the background until the new run finishes.
      const deadline = Date.now() + 20 * 60 * 1000; // 20 min safety cap
      // eslint-disable-next-line no-constant-condition
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 4000));
        let st;
        try { st = (await axiosInstance.get('/admin/drive/status')).data; } catch { continue; }
        setDrive(st);
        const lm = st?.lastMirror;
        const isNew = lm && lm.startedAt > prevStarted;
        if (isNew && lm.status === 'verified') {
          const photos = lm.media ? ` + ${lm.media.filesCopied} photos` : '';
          setMsg({ type: 'success', text: `Verified — Google Drive backup complete (${(lm.files || []).length} files${photos}) in ${lm.folderPath}.` });
          break;
        }
        if (isNew && lm.status === 'failed') {
          setMsg({ type: 'error', text: `Drive copy failed: ${lm.error || 'unknown error'}` });
          break;
        }
        // still running (or run doc not created yet) → keep the "In progress" message
      }
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Could not start Drive copy.' });
    } finally { setDriveBusy(''); }
  };


  const runs = data.runs || [];
  const lastDb = runs.find((r) => r.kind === 'database' && r.status === 'verified' && r.scope === 'full')
    || runs.find((r) => r.kind === 'database' && r.status === 'verified');
  const media = data.media || {};
  const phraseOk = preview && confirmPhrase.trim() === preview.confirmPhrase;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="admin-backup-recovery">
      <button onClick={() => navigate('/admin/dashboard')}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"
        data-testid="backup-back">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <DatabaseBackup className="h-7 w-7 text-indigo-600" /> Backup &amp; Recovery
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Complete, verified snapshots of the whole Find-2-Day system — main database + Calendar
            (including lists) + uploaded photos. Automatic daily backups, stored privately and separately
            from MongoDB. Environment: <span className="font-mono font-medium">{data.env || '…'}</span>.
            The existing Calendar CSV/Excel export is untouched.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-700">
            <input type="checkbox" checked={includeMedia} onChange={(e) => setIncludeMedia(e.target.checked)}
              data-testid="backup-include-media" /> include photos
          </label>
          <button onClick={backupNow} disabled={busy === 'backup'}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
            data-testid="backup-now-btn">
            {busy === 'backup' ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardDriveDownload className="h-4 w-4" />} Backup Now
          </button>
          <button onClick={mediaBackup} disabled={busy === 'media'}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
            data-testid="media-backup-btn">
            {busy === 'media' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Image className="h-4 w-4" />} Media Only
          </button>
          <button onClick={sendTestAlert} disabled={busy === 'test-alert'}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
            title="Send a clearly-marked TEST backup-alert email (sends only, changes nothing)"
            data-testid="send-test-alert-btn">
            {busy === 'test-alert' ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Send Test Alert
          </button>
          <button onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
            data-testid="backup-refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {msg && (
        <div className={`mb-4 text-sm px-3 py-2 rounded-lg flex items-start gap-2 ${msg.type === 'error' ? 'bg-red-50 text-red-700' : msg.type === 'info' ? 'bg-sky-50 text-sky-700' : 'bg-green-50 text-green-700'}`}
          data-testid="backup-message">
          {msg.type === 'error' ? <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> : msg.type === 'info' ? <Loader2 className="h-4 w-4 mt-0.5 shrink-0 animate-spin" /> : <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />}
          {msg.text}
        </div>
      )}

      {/* status cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm" data-testid="card-last-db">
          <p className="text-xs text-slate-500 flex items-center gap-1.5"><Server className="h-4 w-4" /> Last database backup</p>
          {lastDb ? (
            <>
              <p className="text-lg font-bold text-slate-900 mt-1">{fmt(lastDb.finishedAt || lastDb.startedAt)}</p>
              <p className="text-xs text-slate-500">{lastDb.totalDocs} records · {(lastDb.sizeBytes / 1024).toFixed(0)} KB · {lastDb.scope === 'full' ? 'both databases' : lastDb.scope}</p>
            </>
          ) : <p className="text-sm text-slate-400 mt-2">No verified backup yet</p>}
        </div>
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm" data-testid="card-media">
          <p className="text-xs text-slate-500 flex items-center gap-1.5"><Image className="h-4 w-4" /> Photos protected</p>
          <p className="text-lg font-bold text-slate-900 mt-1">{media.filesProtected ?? 0} files</p>
          <p className="text-xs text-slate-500">Last run: {fmt(media.lastRun?.finishedAt || media.lastRun?.startedAt)}</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm flex flex-col justify-between" data-testid="card-media-restore">
          <div>
            <p className="text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Media recovery</p>
            <p className="text-xs text-slate-500 mt-1">Restore missing photos (never overwrites)</p>
          </div>
          <button onClick={mediaRestore} disabled={busy === 'media-restore'}
            className="mt-2 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-60"
            data-testid="media-restore-btn">
            {busy === 'media-restore' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Restore Photos
          </button>
        </div>
      </div>

      {/* Google Drive — second independent backup */}
      {drive?.configured && (
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm mb-6" data-testid="drive-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                <Cloud className="h-4 w-4 text-sky-600" /> Google Drive — second backup
                {drive.connected
                  ? <span className="ml-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3" /> Connected</span>
                  : <span className="ml-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600"><Unlink className="h-3 w-3" /> Not connected</span>}
              </p>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                An independent copy of each verified backup (both databases + manifest{drive.connected ? ' + photos' : ''}) is
                uploaded to your Google Drive under <span className="font-mono">{drive.rootFolder}/{drive.env === 'production' ? 'Production' : (drive.env || '')}/Year/Month/Date</span>.
                Minimal permission (<span className="font-mono">drive.file</span>) — it can only manage files it creates, never your other Drive files. Supabase backups are unaffected.
              </p>
              {drive.connected && (
                <p className="text-xs text-slate-500 mt-1" data-testid="drive-last-status">
                  Account: <span className="font-medium">{drive.email || 'connected'}</span>
                  {drive.inProgress
                    ? <span className="ml-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-sky-100 text-sky-800 border-sky-200"><Loader2 className="h-3 w-3 animate-spin" /> In progress…</span>
                    : drive.lastMirror && <> · Last Drive copy: <span className="font-medium">{fmt(drive.lastMirror.finishedAt || drive.lastMirror.startedAt)}</span> — <span className={drive.lastMirror.status === 'verified' ? 'text-green-700 font-medium' : drive.lastMirror.status === 'running' ? 'text-sky-700' : 'text-red-600'}>{drive.lastMirror.status === 'verified' ? 'Verified' : drive.lastMirror.status}</span>{drive.lastMirror.media ? ` (${drive.lastMirror.media.filesCopied} photos)` : ''}</>}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {!drive.connected ? (
                <button onClick={connectDrive} disabled={driveBusy === 'connect'}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-60"
                  data-testid="drive-connect-btn">
                  {driveBusy === 'connect' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Connect Google Drive
                </button>
              ) : (
                <>
                  <label className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                    <input type="checkbox" checked={driveMedia} onChange={(e) => setDriveMedia(e.target.checked)}
                      data-testid="drive-include-media" /> include photos
                  </label>
                  <button onClick={mirrorNow} disabled={driveBusy === 'mirror' || drive.inProgress}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-60"
                    data-testid="drive-mirror-now-btn">
                    {(driveBusy === 'mirror' || drive.inProgress) ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} {(driveBusy === 'mirror' || drive.inProgress) ? 'In progress…' : 'Copy latest backup to Drive'}
                  </button>
                  <button onClick={disconnectDrive} disabled={driveBusy === 'disconnect'}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
                    data-testid="drive-disconnect-btn">
                    {driveBusy === 'disconnect' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />} Disconnect
                  </button>
                </>
              )}
              <button onClick={loadDrive}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
                data-testid="drive-refresh-btn">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* history */}
      <h2 className="text-sm font-semibold text-slate-700 mb-2">Backup &amp; restore history</h2>
      {loading ? (
        <p className="text-sm text-slate-400 py-6">Loading…</p>
      ) : runs.length === 0 ? (
        <p className="text-sm text-slate-400 py-6" data-testid="backup-empty">No backups yet. Click “Backup Now”.</p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl border border-slate-100 shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">When</th>
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-left px-3 py-2 font-medium">Scope</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Records / files</th>
                <th className="text-left px-3 py-2 font-medium">Size</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`backup-row-${r.id}`}>
                  <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmt(r.finishedAt || r.startedAt)}</td>
                  <td className="px-3 py-2 text-slate-700">{KIND_LABEL[r.kind] || r.kind}{r.trigger === 'auto' && <span className="ml-1 text-[10px] text-slate-400">(auto)</span>}{r.trigger === 'pre-restore' && <span className="ml-1 text-[10px] text-amber-500">(emergency)</span>}</td>
                  <td className="px-3 py-2 text-slate-600">{r.scope || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${STATUS_STYLE[r.status] || 'bg-slate-100 text-slate-600'}`}>
                      {r.status === 'running' && <Clock className="h-3 w-3" />}
                      {(r.status === 'verified' || r.status === 'completed') && <CheckCircle className="h-3 w-3" />}
                      {r.status === 'failed' && <XCircle className="h-3 w-3" />}
                      {r.status}
                    </span>
                    {r.rolledBack === true && <span className="ml-1 text-[10px] text-amber-600">rolled back</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {r.kind === 'database' ? (r.totalDocs != null ? `${r.totalDocs} recs` : '—')
                      : r.fileCount != null ? `${r.fileCount} files`
                        : r.result ? `${r.result.restored ?? r.result.copied ?? 0} affected` : '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.sizeBytes ? `${(r.sizeBytes / 1024).toFixed(0)} KB` : '—'}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.kind === 'database' && r.status === 'verified' && (
                      <>
                        <button onClick={() => openRestore(r)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-100 mr-1"
                          data-testid={`preview-btn-${r.id}`}>
                          <Eye className="h-3.5 w-3.5" /> Preview / Restore
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* restore modal */}
      {restoreBackup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="restore-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-indigo-600" /> Restore preview
              </h3>
              <button onClick={() => setRestoreBackup(null)} className="text-slate-400 hover:text-slate-700" data-testid="restore-close">
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              From backup taken <strong>{fmt(restoreBackup.finishedAt || restoreBackup.startedAt)}</strong>.
              An automatic emergency backup of the current live data is taken <strong>before</strong> anything changes,
              and the system rolls back automatically if the restore fails.
            </p>

            <div className="flex gap-2 mb-3">
              {(restoreBackup.scope === 'full' ? ['full', 'calendar'] : ['calendar']).map((sc) => (
                <button key={sc} onClick={() => changeScope(sc)}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${restoreScope === sc ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600'}`}
                  data-testid={`restore-scope-${sc}`}>
                  {sc === 'full' ? 'Full system (both databases)' : 'Calendar only'}
                </button>
              ))}
            </div>

            {previewLoading || !preview ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
            ) : (
              <>
                {preview.databases.map((dbp) => (
                  <div key={dbp.database} className="mb-3 border border-slate-100 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">{dbp.database} {dbp.label ? `(${dbp.label})` : ''}</div>
                    {dbp.error ? (
                      <p className="text-[11px] text-slate-500 px-3 py-2">{dbp.error}</p>
                    ) : (
                      <>
                        {dbp.willRemoveCollections?.length > 0 && (
                          <p className="text-[11px] text-amber-700 bg-amber-50 px-3 py-1.5 flex items-center gap-1.5">
                            <AlertTriangle className="h-3.5 w-3.5" /> These collections exist now but not in the backup and will be removed: {dbp.willRemoveCollections.join(', ')}
                          </p>
                        )}
                        <table className="min-w-full text-xs">
                          <thead className="text-slate-500"><tr><th className="text-left px-3 py-1">Collection</th><th className="text-left px-3 py-1">In backup</th><th className="text-left px-3 py-1">Live now</th><th className="text-left px-3 py-1">Action</th></tr></thead>
                          <tbody>
                            {(dbp.collections || []).map((c) => (
                              <tr key={c.collection} className="border-t border-slate-50">
                                <td className="px-3 py-1 font-mono text-slate-700">{c.collection}</td>
                                <td className="px-3 py-1">{c.backupCount ?? '—'}</td>
                                <td className="px-3 py-1">{c.liveCount ?? '—'}</td>
                                <td className={`px-3 py-1 ${c.action === 'remove' ? 'text-red-600' : c.action === 'create' ? 'text-green-600' : 'text-slate-500'}`}>{c.action}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>
                ))}

                <label className="inline-flex items-center gap-1.5 text-sm text-slate-700 mb-3">
                  <input type="checkbox" checked={restoreMedia} onChange={(e) => setRestoreMedia(e.target.checked)}
                    data-testid="restore-include-media" /> also restore missing photos afterwards
                </label>

                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-700 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" /> This will overwrite live data. To confirm, type
                    <span className="font-mono font-bold mx-1">{preview.confirmPhrase}</span>
                  </p>
                  <input value={confirmPhrase} onChange={(e) => setConfirmPhrase(e.target.value)}
                    placeholder={preview.confirmPhrase}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                    data-testid="restore-confirm-input" />
                  <button onClick={doRestore} disabled={!phraseOk || restoring}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
                    data-testid="restore-confirm-btn">
                    {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                    Restore {restoreScope === 'full' ? 'entire system' : 'Calendar'} now
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBackupRecovery;
